import { format } from 'date-fns/format'
import { Fragment, type ReactNode, useMemo } from 'react'

import { ButtonRoot } from '@/components/common/Button'
import { CardLink } from '@/components/common/Card'
import { FloatingPopover, FloatingTooltip } from '@/components/common/Floating'
import { InputDropdown } from '@/components/common/Input'
import { LoadingMask } from '@/components/common/LoadingMask'
import { PriceSource } from '@/components/common/PriceSource'
import { ScrollOverflow } from '@/components/common/ScrollOverflow'
import { TableColumnHeader, type TableSortActionType } from '@/components/common/Table'
import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { BRAND_LABELS } from '@/defs/brand'
import { DATEFORMAT_SYSTEM } from '@/defs/date'
import {
  GALLERY_GROUP_LABELS,
  type GalleryColumnSlugType,
  type GallerySortbySlugType,
  isGalleryGroupSlugType,
} from '@/defs/gallery'
import { type IconType } from '@/defs/icon'
import { COLOR_ID_LABELS } from '@/defs/mtg'
import { PRICE_SOURCES, type PriceSourceType } from '@/defs/price'
import { type RecType } from '@/defs/rec'
import { STAT_LABELS } from '@/defs/stat'
import { useCardMap } from '@/hooks/useCardMap'
import { clipboardActions } from '@/store/clipboard'
import { galleryActions } from '@/store/gallery'
import { useReduxDispatch, useReduxSelector } from '@/store/hooks'
import { cn, recordFromArray, slugify } from '@/utils/commonUtil'
import { exportCsv } from '@/utils/csvUtil'
import {
  formatFullNumber,
  formatLift,
  formatPercentage,
  formatPrice,
  formatSalt,
  formatSynergy,
} from '@/utils/formatUtil'

import { useGalleryDisplay } from '../GalleryDisplayProvider/GalleryDisplayProvider'
import { GalleryEmpty } from '../GalleryEmpty/GalleryEmpty'
import { useGalleryGroups } from '../GalleryGroupsProvider/GalleryGroupsProvider'
import { GalleryPagination } from '../GalleryPagination/GalleryPagination'
import { GallerySubheader } from '../GallerySubheader/GallerySubheader'

import styles from './GalleryViewTable.module.scss'
import { GalleryViewTableClipboardButton } from './GalleryViewTableClipboardButton/GalleryViewTableClipboardButton'

type ConfigType = {
  icon?: IconType
  label: string
  render: ({ rec }: { rec: RecType }) => ReactNode
  renderText?: ({ rec }: { rec: RecType }) => string
  sortbySlug: GallerySortbySlugType
}

export const GalleryViewTable = () => {
  const { page } = usePage()
  const { columns, columnsOptions, sortby, sortbyDirection } = useGalleryDisplay()
  const { groups, isEmpty } = useGalleryGroups()
  const cardIds = useMemo(() => groups.flatMap((g) => g.recs.map(({ cardId }) => cardId)), [groups])
  const { cardMap, loading } = useCardMap({ cardIds })
  const dispatch = useReduxDispatch()
  const clipboardCards = useReduxSelector((s) => s.clipboard.cards)

  const priceColumn = (source: PriceSourceType): ConfigType => ({
    icon: BRAND_LABELS[source].icon,
    label: BRAND_LABELS[source].label,
    render: ({ rec: { cardId } }) =>
      cardMap[cardId] && <PriceSource card={cardMap[cardId]} priceSource={source} size='lg' />,
    renderText: ({ rec: { cardId } }) => {
      const price = cardMap[cardId]?.priceSources?.[source]?.price
      return price ? formatPrice(price) : ''
    },
    sortbySlug: `price-${source}`,
  })

  const CONFIG: Record<GalleryColumnSlugType, ConfigType> = {
    cmc: {
      label: 'MV',
      render: ({ rec: { cardId } }) => (cardMap[cardId]?.manaCost ? String(cardMap[cardId].cmc ?? '') : ''),
      sortbySlug: 'mv',
    },
    colorId: {
      label: 'Color ID',
      render: ({ rec: { cardId } }) =>
        cardMap[cardId]?.colorId && <Icon name={COLOR_ID_LABELS[cardMap[cardId].colorId].icon} />,
      renderText: ({ rec: { cardId } }) =>
        (cardMap[cardId]?.colorId && COLOR_ID_LABELS[cardMap[cardId].colorId].label) || '',
      sortbySlug: 'colorId',
    },
    name: {
      label: 'Name',
      render: ({ rec }) => (
        <CardLink className={styles.link} rec={rec}>
          {rec.name}
        </CardLink>
      ),
      renderText: ({ rec: { name } }) => name || '',
      sortbySlug: 'name',
    },
    rarity: {
      icon: 'star',
      label: 'Rarity',
      render: ({ rec: { cardId } }) => {
        const groupSlug = `rarity-${cardMap[cardId]?.rarity}`
        return isGalleryGroupSlugType(groupSlug) ? GALLERY_GROUP_LABELS[groupSlug].label : 'Unknown'
      },
      sortbySlug: 'rarity',
    },
    salt: {
      icon: 'salt',
      label: 'Salt',
      render: ({ rec: { cardId } }) => formatSalt(cardMap[cardId]?.salt),
      sortbySlug: 'salt',
    },
    'stat-count': {
      icon: STAT_LABELS.deckTotal.icon,
      label: STAT_LABELS.deckTotal.label,
      render: ({ rec }) => (rec.numDecks !== undefined ? formatFullNumber(rec.numDecks) : 'N/A'),
      sortbySlug: 'count',
    },
    'stat-inclusion': {
      icon: STAT_LABELS.inclusion.icon,
      label: STAT_LABELS.inclusion.label,
      render: ({ rec }) => `${formatPercentage(rec.inclusion ?? 0)}%`,
      sortbySlug: 'inclusion',
    },
    'stat-lift': {
      icon: STAT_LABELS.lift.icon,
      label: STAT_LABELS.lift.label,
      render: ({ rec }) => (rec.lift !== undefined ? formatLift(rec.lift) : 'N/A'),
      sortbySlug: 'lift',
    },
    'stat-potentialDecks': {
      label: 'Potential Decks',
      render: ({ rec }) => (rec.potentialDecks !== undefined ? formatFullNumber(rec.potentialDecks) : 'N/A'),
      sortbySlug: 'potentialDecks',
    },
    'stat-rank': {
      icon: STAT_LABELS.rank.icon,
      label: STAT_LABELS.rank.label,
      render: ({ rec }) => (rec.rank !== undefined ? formatFullNumber(rec.rank) : 'N/A'),
      sortbySlug: 'rank',
    },
    'stat-synergy': {
      icon: STAT_LABELS.synergy.icon,
      label: STAT_LABELS.synergy.label,
      render: ({ rec }) => (rec.synergy !== undefined ? `${formatSynergy(rec.synergy)}%` : 'N/A'),
      sortbySlug: 'synergy',
    },
    type: { label: 'Type', render: ({ rec: { cardId } }) => cardMap[cardId]?.type, sortbySlug: 'type' },
    ...recordFromArray(PRICE_SOURCES, (ps) => [`price-${ps}`, priceColumn(ps)]),
  }

  const onClickSortby = (sortbySlug: GallerySortbySlugType, action: TableSortActionType) => {
    switch (action) {
      case 'apply': {
        dispatch(galleryActions.setSortbyDirection('desc'))
        dispatch(galleryActions.setSortby(sortbySlug))
        break
      }
      case 'reverse': {
        dispatch(galleryActions.setSortbyDirection('asc'))
        break
      }
      case 'reset': {
        dispatch(galleryActions.resetSortby())
        break
      }
    }
  }

  const onClickColumn = (column: GalleryColumnSlugType) => {
    dispatch(
      galleryActions.setColumns(columns.includes(column) ? columns.filter((c) => c !== column) : [...columns, column]),
    )
  }

  const onClickExportCsv = () => {
    const rows = [
      columns.map((column) => String(CONFIG[column].label)),
      ...groups.flatMap((g) =>
        g.recs.map((rec) =>
          columns.map((column) => {
            const { render, renderText } = CONFIG[column]
            if (renderText) return renderText({ rec })
            const value = render({ rec })
            if (typeof value === 'string') return value
            return ''
          }),
        ),
      ),
    ]

    const filename = `${format(new Date(), DATEFORMAT_SYSTEM)}-${slugify(page?.metadata?.title || 'download')}.csv`

    exportCsv({ filename, rows })
  }

  const onClickClipboardAll = () => {
    const cards = groups
      .flatMap((g) => g.recs)
      .flatMap(({ cardId, name, partnerRef }) => {
        const cards = [{ cardId, name }]
        if (partnerRef) cards.push(partnerRef)
        return cards
      })

    const clipboardCardKeys = new Set(clipboardCards.map(({ cardId }) => cardId))
    const overlap = cards.filter(({ cardId }) => clipboardCardKeys.has(cardId))

    if (overlap.length > 0) {
      dispatch(clipboardActions.remove(cards))
      return
    }

    dispatch(clipboardActions.add(cards))
  }

  return (
    <div className={styles.container}>
      <GallerySubheader icon='table' title='Table View'>
        <div className={styles.actions}>
          <FloatingPopover
            content={
              <InputDropdown
                onSelect={onClickColumn}
                optionGroups={[
                  {
                    label: 'Edit Columns',
                    options: columnsOptions
                      .filter((c) => c !== 'name')
                      .map((column) => ({
                        label: CONFIG[column].label,
                        value: column,
                      })),
                  },
                ]}
                values={columns}
              />
            }
          >
            <ButtonRoot aria-label='Edit Columns'>
              <Icon name='cog' />
            </ButtonRoot>
          </FloatingPopover>
          <FloatingTooltip content={<IconWithLabel iconBefore='download'>Export as CSV</IconWithLabel>}>
            <ButtonRoot aria-label='Export CSV' onClick={onClickExportCsv}>
              <Icon name='download' />
            </ButtonRoot>
          </FloatingTooltip>
        </div>
      </GallerySubheader>
      {isEmpty ? (
        <GalleryEmpty />
      ) : (
        <div className={styles.scrollContainer}>
          <ScrollOverflow>
            <table className={styles.table}>
              <thead>
                <tr>
                  <td>
                    <ButtonRoot aria-label='Add or remove all cards from clipboard' onClick={onClickClipboardAll}>
                      <Icon name='paperclip' />
                    </ButtonRoot>
                  </td>
                  {columns.map((column) => {
                    const { icon, label, sortbySlug } = CONFIG[column]

                    return (
                      <th className={cn(styles.heading, { styles, variant: column })} key={column}>
                        <TableColumnHeader
                          icon={icon}
                          isSortable
                          isSortby={sortby === sortbySlug}
                          label={label}
                          onClick={(action) => onClickSortby(sortbySlug, action)}
                          sortbyDirection={sortbyDirection}
                        />
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {groups.map(
                  (group, i) =>
                    group.recs.length > 0 && (
                      <Fragment key={i}>
                        <tr>
                          <th className={styles.group} colSpan={columns.length + 1}>
                            <IconWithLabel iconBefore={group.icon} wrapNone>
                              {group.label}
                            </IconWithLabel>
                          </th>
                        </tr>
                        {group.recs.map((rec, i) => (
                          <tr key={i}>
                            <td>
                              <GalleryViewTableClipboardButton rec={rec} />
                            </td>
                            {columns.map((column) => (
                              <td key={column}>{CONFIG[column].render({ rec })}</td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ),
                )}
              </tbody>
            </table>
          </ScrollOverflow>
        </div>
      )}
      <GalleryPagination />
      <LoadingMask loading={loading} />
    </div>
  )
}
